import { motion } from "framer-motion";
import { Sun, Zap, BarChart3, MapPin, Thermometer, Shield, Cpu, Eye } from "lucide-react";
import solarFarmAerial from "../assets/solar-farm-aerial.jpg";
import solarImg from "../assets/solar-energy.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import StatsGrid from "../components/StatsGrid";
import EnergyFlowSVG from "../components/EnergyFlowSVG";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const SolarEnergy = () => (
  <div>
    <PageHero
      eyebrow="Solar Energy Division"
      title={<>Transforming Sunlight into <span className="energy-gradient-text">Sustainable Power</span></>}
      description="Our solar division manages 22 utility-scale installations across Southern Europe and North Africa, totaling 330 MW of peak capacity using bifacial panels with single-axis tracking."
      backgroundImage={solarFarmAerial}
    />

    <Section>
      <StatsGrid stats={[
        { value: "330 MWp", label: "Peak Solar Capacity" },
        { value: "22", label: "Active Solar Sites" },
        { value: "22%+", label: "Panel Efficiency" },
        { value: "1.2M", label: "Solar Panels Deployed" },
      ]} />
    </Section>

    {/* Overview */}
    <Section className="bg-secondary/20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fadeUp} className="relative rounded-2xl overflow-hidden glow-box order-2 lg:order-1">
          <img src={solarImg} alt="Solar panels close-up" className="w-full h-[400px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2">
            {[
              { v: "22%+", l: "Efficiency" },
              { v: "35%", l: "Tracking Boost" },
              { v: "0.3%/yr", l: "Degradation" },
            ].map((s, i) => (
              <div key={i} className="p-2 rounded-lg glass-card text-center">
                <div className="font-display text-xs font-bold text-primary">{s.v}</div>
                <div className="text-[9px] text-muted-foreground">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="order-1 lg:order-2">
          <span className="font-display text-xs tracking-[0.3em] uppercase text-primary mb-3 block">Technology</span>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Advanced Photovoltaic Systems</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            AeroVolt's solar division deploys LONGi Hi-MO 6 bifacial modules — Tier-1 rated panels with 22.3% conversion efficiency and an industry-leading 0.3% annual degradation rate. Bifacial technology captures reflected light from the ground surface, increasing energy yield by up to 12% compared to monofacial alternatives.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Every installation uses Nextracker NX Horizon single-axis tracking systems that follow the sun's arc from east to west throughout the day. This intelligent tracking increases daily energy capture by up to 35% compared to fixed-tilt installations.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Our sites are located in regions with 1,800+ kWh/m²/year solar irradiance — Southern Spain, Portugal, Morocco, and Tunisia — ensuring maximum energy production across all months.
          </p>
        </motion.div>
      </div>
    </Section>

    <div className="overflow-hidden"><EnergyFlowSVG className="h-16 opacity-30" /></div>

    {/* Features */}
    <Section>
      <SectionHeading eyebrow="Capabilities" title="Solar Technology Stack" description="State-of-the-art equipment and systems for maximum energy yield." />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: Sun, title: "Bifacial Panels", desc: "LONGi Hi-MO 6 modules capture direct and reflected light for 12% additional energy yield from ground albedo." },
          { icon: Cpu, title: "Smart Tracking", desc: "Nextracker NX Horizon systems track the sun east-to-west, boosting daily production by 35% vs fixed-tilt arrays." },
          { icon: Zap, title: "String Inverters", desc: "Huawei SUN2000 string inverters with 99% efficiency, MPPT optimization, and IV curve diagnosis per string." },
          { icon: Eye, title: "Satellite Monitoring", desc: "Real-time satellite-linked monitoring with AI anomaly detection, thermal imaging, and per-panel performance tracking." },
          { icon: BarChart3, title: "Performance Ratio 85%+", desc: "Industry-leading performance ratios achieved through optimized design, premium components, and continuous monitoring." },
          { icon: Shield, title: "30-Year Warranty", desc: "Panels warranted for 30 years with guaranteed 87.4% output at year 30 — one of the longest warranties in the industry." },
          { icon: Thermometer, title: "Desert-Rated", desc: "All components rated for extreme environments — UV resistance, sand ingress protection, and 45°C+ ambient operation." },
          { icon: MapPin, title: "High Irradiance Sites", desc: "All sites selected in 1,800+ kWh/m²/year zones with 10+ years of satellite irradiance data validation." },
        ].map((f, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }} className="p-6 rounded-2xl glass-card glass-card-hover transition-all duration-500 group">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
              <f.icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-display text-sm font-semibold mb-2">{f.title}</h3>
            <p className="text-muted-foreground text-xs leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Solar Sites */}
    <Section className="bg-secondary/20">
      <SectionHeading eyebrow="Active Sites" title="Solar Farm Portfolio" />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { name: "Andalusia Sol", location: "Seville, Spain", capacity: "45 MWp", panels: "120,000", status: "Operational" },
          { name: "Algarve Light", location: "Faro, Portugal", capacity: "30 MWp", panels: "80,000", status: "Operational" },
          { name: "Atlas Prime", location: "Ouarzazate, Morocco", capacity: "50 MWp", panels: "133,000", status: "Operational" },
          { name: "Mediterranean Sun", location: "Murcia, Spain", capacity: "35 MWp", panels: "93,000", status: "Operational" },
          { name: "Sahel Gateway", location: "Tunis, Tunisia", capacity: "25 MWp", panels: "67,000", status: "Operational" },
          { name: "Iberian Dawn", location: "Badajoz, Spain", capacity: "40 MWp", panels: "107,000", status: "Commissioning" },
        ].map((site, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-5 rounded-xl glass-card glass-card-hover transition-all duration-500">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-display text-sm font-semibold">{site.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${site.status === "Operational" ? "bg-primary/10 text-primary" : "bg-gold/10 text-gold"}`}>
                {site.status}
              </span>
            </div>
            <div className="space-y-1.5 text-xs text-muted-foreground">
              <div className="flex justify-between"><span>Location</span><span className="text-foreground">{site.location}</span></div>
              <div className="flex justify-between"><span>Capacity</span><span className="text-foreground">{site.capacity}</span></div>
              <div className="flex justify-between"><span>Panels</span><span className="text-foreground">{site.panels}</span></div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  </div>
);

export default SolarEnergy;
